// API keys and Claude's grants: the caller's own (Account) and an admin's view of one
// user's (the user drawer's API access section). docs/plans/claude-connector.md §5.
//
// Domain-backed on two domains:
// - `identity_rbac`: the 0031 revision triggers bump it when a credential is created,
//   revoked, renamed or expires, and a role change, which can revoke every credential, bumps
//   it too;
// - `configuration`: the response carries the two switches (`edge.api_keys_enabled`,
//   `edge.mcp_enabled`), and an admin flipping one bumps only this domain.
// `last_used_at` bumps neither, by design (every key request would otherwise churn every
// identity read), so the screens that show it refresh a list older than a minute when they
// mount or regain focus (`useRefreshWhenOlderThan`). Neither payload carries personal data:
// a summary never holds a secret, an IP address or a user agent (shared::CredentialSummary).
//
// The one response that carries a secret, `POST /me/api-keys`, is never a resource: its
// secret goes straight to the sheet that shows it once (`useCreateApiKey`), and nothing
// caches it.

import { api } from '@/api/client';
import { defineQuery } from '@/api/resource';
import type { CredentialListResponse, OauthConsentView } from '@/api/types';

/** `GET /me/credentials`: the caller's keys and grants, the two switches and their role's cap. */
export const myCredentials = defineQuery({
  namespace: 'authoritative',
  name: 'credentials.mine',
  domains: ['identity_rbac', 'configuration'],
  keyOf: () => [],
  request: (_: void, signal) => api.get<CredentialListResponse>('/me/credentials', { signal }),
});

/** `GET /admin/users/{id}/credentials`: what an unscoped admin sees of one user's. */
export const adminUserCredentials = defineQuery({
  namespace: 'authoritative',
  name: 'admin.userCredentials',
  domains: ['identity_rbac', 'configuration'],
  keyOf: (userId: string) => [userId],
  request: (userId: string, signal) =>
    api.get<CredentialListResponse>(
      `/admin/users/${encodeURIComponent(userId)}/credentials`,
      { signal },
    ),
});

/**
 * `GET /oauth-consent/{id}`: the request an app (Claude) is waiting on, for the consent page.
 *
 * Volatile, not domain-backed: the page lives on a public path, which never asks for a revision
 * manifest, and the request is gone ten minutes after it was made or the moment it is answered.
 * `retry: false`: a 404 means "already answered or lapsed", a stable state the screen words
 * itself ("start again from Claude"); a retry would only hold it back. Any other failure offers
 * Retry.
 */
export const oauthConsent = defineQuery({
  namespace: 'private',
  name: 'oauth.consent',
  volatile: true,
  retry: false,
  // Coming back to the tab re-reads it, so a connector an admin switched back on meanwhile
  // shows without a reload (the page also offers "Check again").
  refetchOnWindowFocus: true,
  keyOf: (requestId: string) => [requestId],
  request: (requestId: string, signal) =>
    api.get<OauthConsentView>(`/oauth-consent/${encodeURIComponent(requestId)}`, { signal }),
});
